import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import {
  BodyText,
  Callout,
  Collapsible,
  Skeleton,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components";
import { trackEvent } from "@/lib/tracking";
import { hasMarkdownFormatting } from "@/lib/utils";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { useParams, useSearch } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import type { z } from "zod";
import { AssessmentAction, useAssessment } from "../network/useAssessment";
import { useCreateAssessment } from "../network/useCreateAssessment";
import { useDeleteAssessment } from "../network/useDeleteAssessment";
import { useUpdateAssessment } from "../network/useUpdateAssessment";
import { AssessmentEditor } from "./AssessmentEditor";
import { AssessmentGenerationControls } from "./AssessmentGenerationControls";
import type { editAssessmentSchema } from "./schemas";
import { useAssessmentGeneration } from "./useAssessmentGeneration";

const AI_ASSESSMENT_GENERATION_FEATURE_FLAG = "ai-assessment-generation-2-0";

export const Assessment = ({
  encounterId,
  readOnly = false,
}: {
  encounterId?: string;
  readOnly?: boolean;
}) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const searchParams = useSearch({ from: "/patient/$patientId" });
  const motherEncounterId = searchParams.encounterId as string;
  const isSingleEncounter = motherEncounterId === encounterId;

  const [isEditing, setIsEditing] = useState(false);
  const [open, setOpen] = useState(true);

  const {
    data: assessmentData,
    isFetching: isFetchingAssessment,
    isError: isErrorLoadingAssessment,
  } = useAssessment(encounterId);
  const assessment = assessmentData?.assessment;

  const { data: isAiAssessmentGenerationEnabled } = useConfigCatFeatureFlag(
    AI_ASSESSMENT_GENERATION_FEATURE_FLAG,
  );

  const createAssessment = useCreateAssessment({
    patientId,
    encounterId: encounterId,
    motherEncounterId,
  });

  const updateAssessment = useUpdateAssessment({
    patientId,
    encounterId,
    motherEncounterId,
    docRefId: assessment?.id,
  });

  const assessmentGeneration = useAssessmentGeneration({
    assessmentData,
    patientId,
    encounterId,
    enabled: Boolean(isAiAssessmentGenerationEnabled),
    motherEncounterId,
  });
  const deleteAssessment = useDeleteAssessment({
    patientId,
    motherEncounterId,
    encounterId,
    docRefId: assessment?.id,
  });

  const canEditAssessment =
    assessmentData?.availableActions?.availableActions.some(
      ({ action, unavailableReason }) =>
        action === AssessmentAction.UPDATE && !unavailableReason,
    );
  const canDirectlyVoidAssessment =
    isSingleEncounter &&
    Boolean(assessment?.id) &&
    assessmentData?.availableActions?.availableActions.some(
      ({ action, unavailableReason }) =>
        action === AssessmentAction.VOID && !unavailableReason,
    );
  const isAssessmentSaving =
    createAssessment.isPending || updateAssessment.isPending;
  const isAssessmentEditingDisabled =
    readOnly ||
    assessmentGeneration.isInProgress ||
    (assessment != null && !canEditAssessment);

  const onSubmit = async (values: z.infer<typeof editAssessmentSchema>) => {
    if (readOnly || assessmentGeneration.isInProgress) {
      return;
    }

    trackEvent(TRACKERS.SAVE_ASSESSMENT);

    try {
      if (assessment) {
        await updateAssessment.mutateAsync({
          assessmentText: values.text,
        });
      } else {
        await createAssessment.mutateAsync({
          encounterId,
          patientId,
          assessmentText: values.text,
        });
      }

      setIsEditing(false);
    } catch (error) {
      toast.error(
        assessment
          ? "Failed to update assessment information"
          : "Failed to add assessment information",
      );
    }
  };

  const handleClickVoidAssessment = async () => {
    if (!canDirectlyVoidAssessment || deleteAssessment.isPending) {
      return;
    }

    try {
      await deleteAssessment.mutateAsync();
    } catch {
      toast.error("Failed to void assessment. Please try again.");
    }
  };

  if (isErrorLoadingAssessment) {
    return (
      <Callout
        className="w-full"
        variant="danger"
        title="Something went wrong!"
        description="Failed to load assessment."
      />
    );
  }

  return (
    <Collapsible
      title="Assessment"
      forcedDropdownActions={
        canDirectlyVoidAssessment
          ? [
              {
                label: "Delete Assessment",
                onClick: handleClickVoidAssessment,
                icon: <Trash2 size={16} />,
                variant: "danger",
                disabled: readOnly || deleteAssessment.isPending,
              },
            ]
          : []
      }
      dropdownActionsLoading={deleteAssessment.isPending}
      headerSize={isSingleEncounter ? "md" : "sm"}
      open={open}
      onOpenChange={(open) => {
        trackEvent(
          open ? TRACKERS.EXPAND_ASSESSMENT : TRACKERS.COLLAPSE_ASSESSMENT,
        );
        setOpen(open);
      }}
    >
      <div className="flex flex-col gap-2">
        {isEditing ? (
          <AssessmentEditor
            onSubmit={onSubmit}
            onCancel={() => {
              trackEvent(TRACKERS.CANCEL_EDITING_ASSESSMENT);
              setIsEditing(false);
            }}
            isLoading={isAssessmentSaving}
            disabled={readOnly || assessmentGeneration.isInProgress}
            documentReference={assessment}
          />
        ) : isFetchingAssessment || isAssessmentSaving ? (
          <div className="grid gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
              <Skeleton className="h-6" key={i} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <EditableRecordButton
              onClick={() => {
                trackEvent(TRACKERS.EDIT_ASSESSMENT);
                setIsEditing(true);
              }}
              disabled={isAssessmentEditingDisabled}
            >
              {assessment?.description ? (
                hasMarkdownFormatting(assessment.description) ? (
                  <MarkdownRenderer content={assessment.description} compact />
                ) : (
                  <BodyText size="sm" preserveLineBreaks>
                    {assessment.description}
                  </BodyText>
                )
              ) : assessment ? (
                <BodyText
                  size="sm"
                  textColor="primary"
                  className="whitespace-pre-wrap"
                >
                  Click to edit assessment
                </BodyText>
              ) : (
                <BodyText
                  size="sm"
                  textColor="primary"
                  className="whitespace-pre-wrap"
                >
                  Click to add assessment
                </BodyText>
              )}
            </EditableRecordButton>
          </div>
        )}
        <AssessmentGenerationControls
          isAssessmentSaving={isAssessmentSaving}
          encounterId={encounterId}
          feedbackBtSpanId={assessmentGeneration.feedbackBtSpanId}
          hasPreviouslyGeneratedAssessment={
            assessmentGeneration.hasPreviouslyGeneratedAssessment
          }
          isAssessmentFlagged={assessmentGeneration.isAssessmentFlagged}
          isInProgress={assessmentGeneration.isInProgress}
          patientId={patientId}
          readOnly={readOnly}
          shouldShowHigherReasoningNotice={
            assessmentGeneration.shouldShowHigherReasoningNotice
          }
          showControls={assessmentGeneration.showControls}
          start={assessmentGeneration.start}
          status={assessmentGeneration.status}
          unavailableReason={assessmentGeneration.unavailableReason}
        />
      </div>
    </Collapsible>
  );
};
